import {
  ArrowLeft,
  ArrowRight,
  BarChart3,
  Building2,
  Check,
  ChevronRight,
  CircleHelp,
  Crosshair,
  Eye,
  EyeOff,
  Globe2,
  Languages,
  Layers3,
  Map,
  MapPin,
  Pause,
  Play,
  Presentation,
  RefreshCw,
  Route,
  Search,
  SlidersHorizontal,
  Sparkles,
  Store,
  Users,
  X,
} from "lucide-react";
import maplibregl from "maplibre-gl";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

const MAP_STYLE = "https://tiles.openfreemap.org/styles/positron";

const ROLE_OPTIONS = [
  {
    id: "urban",
    score: "score_urban",
    zh: "城市综合店",
    en: "Urban multi-sport",
    shortZh: "城市店",
    shortEn: "Urban",
    icon: Building2,
  },
  {
    id: "family",
    score: "score_family",
    zh: "家庭目的店",
    en: "Family destination",
    shortZh: "目的店",
    shortEn: "Destination",
    icon: Users,
  },
  {
    id: "community",
    score: "score_community",
    zh: "社区服务点",
    en: "Community / pickup",
    shortZh: "社区点",
    shortEn: "Community",
    icon: MapPin,
  },
  {
    id: "replacement",
    score: "score_replacement",
    zh: "旧店替换",
    en: "Store replacement",
    shortZh: "替换",
    shortEn: "Replace",
    icon: RefreshCw,
  },
  {
    id: "sport",
    score: "score_sport",
    zh: "专项运动实验",
    en: "Sport lab exploration",
    shortZh: "专项店",
    shortEn: "Sport lab",
    icon: Route,
    experimental: true,
  },
];

const COUNTRY_ORDER = ["fr", "de", "gb", "sg", "tw"];
const STORY_ORDER = ["blind", "location", "global"];

const EMPTY_FC = { type: "FeatureCollection", features: [] };

function bilingual(lang, zh, en) {
  return lang === "zh" ? zh : en;
}

function formatNumber(value, lang = "zh") {
  const number = Number(value || 0);
  if (lang === "zh" && number >= 10000) {
    return `${(number / 10000).toFixed(number >= 100000 ? 0 : 1)}万`;
  }
  return new Intl.NumberFormat(lang === "zh" ? "zh-CN" : "en-US", {
    maximumFractionDigits: 0,
  }).format(number);
}

function formatDistance(value, lang = "zh") {
  const number = Number(value || 0);
  if (number >= 1000) return `${(number / 1000).toFixed(1)} km`;
  return `${Math.round(number)} m`;
}

function safeFeatureCollection(value) {
  return value?.type === "FeatureCollection" ? value : EMPTY_FC;
}

function featureCenter(feature) {
  const properties = feature?.properties || {};
  return [
    Number(properties.center_lng || 0),
    Number(properties.center_lat || 0),
  ];
}

function readInitialState() {
  const params = new URLSearchParams(window.location.search);
  return {
    view: ["location", "blind", "global"].includes(params.get("view"))
      ? params.get("view")
      : "location",
    lang: params.get("lang") === "en" ? "en" : "zh",
    city: params.get("city") === "beijing" ? "beijing" : "shanghai",
    role: ROLE_OPTIONS.some((item) => item.id === params.get("role"))
      ? params.get("role")
      : "urban",
    country: COUNTRY_ORDER.includes(params.get("country"))
      ? params.get("country")
      : "fr",
    snapshot: params.get("snapshot") === "202501" ? "202501" : "20260730",
    presentation: params.get("story") === "1",
  };
}

function useSiteData() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    Promise.all([
      fetch("/data/meta.json").then((response) => response.json()),
      fetch("/data/china_stores.geojson").then((response) => response.json()),
      fetch("/data/candidates_shanghai.geojson").then((response) =>
        response.json(),
      ),
      fetch("/data/candidates_beijing.geojson").then((response) =>
        response.json(),
      ),
      fetch("/data/population_similarity.json").then((response) =>
        response.json(),
      ),
      fetch("/data/population_twins.json").then((response) => response.json()),
      fetch("/data/global_networks.json").then((response) => response.json()),
      Promise.all(
        ["fr", "de", "gb"].map((country) =>
          fetch(`/data/population_${country}.geojson`).then((response) => {
            if (!response.ok) throw new Error(`Population grid ${country} failed`);
            return response.json();
          }),
        ),
      ),
    ])
      .then(
        ([
          meta,
          stores,
          shanghai,
          beijing,
          similarity,
          twins,
          global,
          populationGrids,
        ]) => {
          setData({
            meta,
            stores: safeFeatureCollection(stores),
            candidates: {
              shanghai: safeFeatureCollection(shanghai),
              beijing: safeFeatureCollection(beijing),
            },
            similarity,
            twins,
            global,
            populationGrids: {
              fr: safeFeatureCollection(populationGrids[0]),
              de: safeFeatureCollection(populationGrids[1]),
              gb: safeFeatureCollection(populationGrids[2]),
            },
          });
          window.__APP_READY__ = true;
        },
      )
      .catch((reason) => {
        setError(reason);
        window.__APP_READY__ = false;
      });
  }, []);

  return { data, error };
}

function MapCanvas({
  lang,
  mode = "location",
  center,
  zoom,
  candidates = EMPTY_FC,
  points = EMPTY_FC,
  populationGrid = EMPTY_FC,
  showPopulation = false,
  roleScore = "score_urban",
  selectedId,
  onSelectCandidate,
  showActive = true,
  showClosed = true,
  storyKey = "",
}) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const loadedRef = useRef(false);
  const onSelectRef = useRef(onSelectCandidate);
  const popupRef = useRef(null);

  useEffect(() => {
    onSelectRef.current = onSelectCandidate;
  }, [onSelectCandidate]);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return undefined;

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: MAP_STYLE,
      center,
      zoom,
      attributionControl: false,
      maxPitch: 55,
    });
    mapRef.current = map;
    // Expose the live map to deterministic QA. This is intentionally attached
    // to the container rather than window so multiple maps can coexist safely.
    containerRef.current._map = map;
    map.addControl(
      new maplibregl.NavigationControl({ showCompass: false }),
      "bottom-right",
    );

    // `load` waits for a visually complete first frame, including remote
    // basemap tiles. A slow/failed OpenFreeMap tile used to block every custom
    // layer, making the population grid disappear with the basemap. The style
    // is enough to install our GeoJSON sources, so do that as soon as it is
    // ready and let basemap tiles continue loading independently.
    map.on("style.load", () => {
      if (map.getSource("population-grid")) return;
      loadedRef.current = true;
      map.addSource("population-grid", {
        type: "geojson",
        data: populationGrid,
      });
      map.addLayer({
        id: "population-grid-fill",
        type: "fill",
        source: "population-grid",
        layout: { visibility: showPopulation ? "visible" : "none" },
        paint: {
          "fill-color": [
            "step",
            ["coalesce", ["get", "population"], 0],
            "rgba(255,255,255,0)",
            500,
            "#fff1b8",
            2500,
            "#f6c85f",
            10000,
            "#ef7f45",
            40000,
            "#c83d3d",
            120000,
            "#6f1d3b",
          ],
          "fill-opacity": 0.72,
          "fill-outline-color": "rgba(91,47,31,.18)",
        },
      });
      map.addSource("candidates", { type: "geojson", data: candidates });
      map.addLayer({
        id: "candidate-fill",
        type: "fill",
        source: "candidates",
        paint: {
          "fill-color": [
            "interpolate",
            ["linear"],
            ["coalesce", ["get", roleScore], 0],
            35,
            "#dfe8df",
            55,
            "#8dc9a8",
            75,
            "#1f8f63",
            92,
            "#07563a",
          ],
          "fill-opacity": [
            "interpolate",
            ["linear"],
            ["zoom"],
            8,
            0.62,
            13,
            0.36,
          ],
        },
      });
      map.addLayer({
        id: "candidate-line",
        type: "line",
        source: "candidates",
        paint: {
          "line-color": "rgba(7,86,58,.88)",
          "line-width": ["interpolate", ["linear"], ["zoom"], 8, 0.7, 14, 2],
          "line-opacity": 0.8,
        },
      });
      map.addLayer({
        id: "candidate-selected",
        type: "line",
        source: "candidates",
        filter: ["==", ["get", "candidate_id"], "__none__"],
        paint: {
          "line-color": "#f3b61f",
          "line-width": 4,
          "line-opacity": 1,
        },
      });

      map.addSource("points", { type: "geojson", data: points });
      map.addLayer({
        id: "active-stores",
        type: "circle",
        source: "points",
        filter: ["==", ["get", "status"], "正常营业"],
        paint: {
          "circle-radius": ["interpolate", ["linear"], ["zoom"], 4, 3, 13, 7],
          "circle-color": "#087a52",
          "circle-stroke-color": "#ffffff",
          "circle-stroke-width": 1.5,
          "circle-opacity": 0.92,
        },
      });
      map.addLayer({
        id: "closed-stores",
        type: "circle",
        source: "points",
        filter: ["==", ["get", "status"], "店铺关闭"],
        paint: {
          "circle-radius": ["interpolate", ["linear"], ["zoom"], 4, 3, 13, 7],
          "circle-color": "#d94b3d",
          "circle-stroke-color": "#ffffff",
          "circle-stroke-width": 1.5,
          "circle-opacity": 0.78,
        },
      });
      map.addLayer({
        id: "paused-stores",
        type: "circle",
        source: "points",
        filter: ["==", ["get", "status"], "暂停营业"],
        paint: {
          "circle-radius": 5,
          "circle-color": "#7c8580",
          "circle-stroke-color": "#ffffff",
          "circle-stroke-width": 1.5,
        },
      });
      map.addLayer({
        id: "global-stores",
        type: "circle",
        source: "points",
        filter: ["!", ["has", "status"]],
        paint: {
          "circle-radius": ["interpolate", ["linear"], ["zoom"], 3, 3, 10, 7],
          "circle-color": "#1178a8",
          "circle-stroke-color": "#ffffff",
          "circle-stroke-width": 1.2,
          "circle-opacity": 0.88,
        },
      });

      map.on("click", "candidate-fill", (event) => {
        const feature = event.features?.[0];
        if (!feature) return;
        onSelectRef.current?.(feature.properties?.candidate_id);
      });
      map.on("mouseenter", "candidate-fill", () => {
        map.getCanvas().style.cursor = "pointer";
      });
      map.on("mouseleave", "candidate-fill", () => {
        map.getCanvas().style.cursor = "";
      });

      ["active-stores", "closed-stores", "paused-stores", "global-stores"].forEach(
        (layer) => {
          map.on("click", layer, (event) => {
            const feature = event.features?.[0];
            if (!feature) return;
            popupRef.current?.remove();
            const props = feature.properties || {};
            const status = props.status
              ? `<div class="map-popup-status">${props.status}</div>`
              : "";
            popupRef.current = new maplibregl.Popup({
              closeButton: true,
              maxWidth: "280px",
            })
              .setLngLat(feature.geometry.coordinates)
              .setHTML(
                `<div class="map-popup"><strong>${props.name || "Decathlon"}</strong>${status}<span>${props.city || props.address || ""}</span></div>`,
              )
              .addTo(map);
          });
          map.on("mouseenter", layer, () => {
            map.getCanvas().style.cursor = "pointer";
          });
          map.on("mouseleave", layer, () => {
            map.getCanvas().style.cursor = "";
          });
        },
      );

      map.on("click", "population-grid-fill", (event) => {
        const feature = event.features?.[0];
        if (!feature) return;
        popupRef.current?.remove();
        const value = Number(feature.properties?.population || 0);
        popupRef.current = new maplibregl.Popup({ closeButton: true, maxWidth: "240px" })
          .setLngLat(event.lngLat)
          .setHTML(
            `<div class="map-popup"><strong>${value.toLocaleString()} ${lang === "zh" ? "人" : "people"}</strong><span>${lang === "zh" ? "约 10 公里展示网格" : "Approx. 10 km display cell"}</span></div>`,
          )
          .addTo(map);
      });
      map.on("mouseenter", "population-grid-fill", () => {
        map.getCanvas().style.cursor = "crosshair";
      });
      map.on("mouseleave", "population-grid-fill", () => {
        map.getCanvas().style.cursor = "";
      });

      if (containerRef.current) {
        containerRef.current.dataset.operationalLayersReady = "true";
        containerRef.current.dataset.populationFeatureCount = String(
          populationGrid.features.length,
        );
      }
    });

    return () => {
      popupRef.current?.remove();
      map.remove();
      if (containerRef.current) {
        delete containerRef.current._map;
      }
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !loadedRef.current) return;
    map.getSource("candidates")?.setData(candidates);
  }, [candidates]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !loadedRef.current) return;
    map.getSource("points")?.setData(points);
  }, [points]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !loadedRef.current) return;
    map.getSource("population-grid")?.setData(populationGrid);
  }, [populationGrid]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !loadedRef.current || !map.getLayer("population-grid-fill")) return;
    map.setLayoutProperty(
      "population-grid-fill",
      "visibility",
      showPopulation ? "visible" : "none",
    );
  }, [showPopulation]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !loadedRef.current || !map.getLayer("candidate-fill")) return;
    map.setPaintProperty("candidate-fill", "fill-color", [
      "interpolate",
      ["linear"],
      ["coalesce", ["get", roleScore], 0],
      35,
      "#dfe8df",
      55,
      "#8dc9a8",
      75,
      "#1f8f63",
      92,
      "#07563a",
    ]);
  }, [roleScore]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !loadedRef.current) return;
    map.setLayoutProperty(
      "active-stores",
      "visibility",
      showActive ? "visible" : "none",
    );
    map.setLayoutProperty(
      "closed-stores",
      "visibility",
      showClosed ? "visible" : "none",
    );
  }, [showActive, showClosed]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !loadedRef.current || !selectedId) return;
    map.setFilter("candidate-selected", [
      "==",
      ["get", "candidate_id"],
      selectedId,
    ]);
    const feature = candidates.features.find(
      (item) => item.properties?.candidate_id === selectedId,
    );
    if (feature) {
      map.easeTo({
        center: featureCenter(feature),
        zoom: Math.max(map.getZoom(), mode === "location" ? 11.4 : 9),
        duration: 700,
      });
    }
  }, [selectedId]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !loadedRef.current) return;
    map.easeTo({ center, zoom, duration: 700 });
  }, [center?.[0], center?.[1], zoom, storyKey]);

  return (
    <div className="map-canvas" ref={containerRef}>
      <div className="map-source">
        {mode === "global"
          ? bilingual(
              lang,
              "底图：OpenFreeMap · 人口：WorldPop 2020 · 门店：极海全球 POI 快照",
              "Basemap: OpenFreeMap · Population: WorldPop 2020 · Stores: Jihai global POI snapshots",
            )
          : bilingual(
              lang,
              "底图：OpenFreeMap · 数据：极海",
              "Basemap: OpenFreeMap · Data: Jihai",
            )}
      </div>
    </div>
  );
}

function ToggleRow({ checked, onChange, icon: Icon, label, color }) {
  return (
    <button
      className={`toggle-row ${checked ? "checked" : ""}`}
      onClick={() => onChange(!checked)}
      type="button"
    >
      <span className="legend-symbol" style={{ background: color }} />
      <Icon size={16} aria-hidden="true" />
      <span>{label}</span>
      {checked ? <Eye size={16} /> : <EyeOff size={16} />}
    </button>
  );
}

function ScoreBadge({ score }) {
  const value = Math.round(Number(score || 0));
  return (
    <div
      className="score-badge"
      style={{ "--score": `${Math.max(0, Math.min(value, 100)) * 3.6}deg` }}
    >
      <span>{value}</span>
    </div>
  );
}

function CandidateRow({
  feature,
  rank,
  lang,
  role,
  selected,
  onSelect,
}) {
  const props = feature.properties || {};
  const score = props[role.score];
  return (
    <button
      type="button"
      className={`candidate-row ${selected ? "selected" : ""}`}
      onClick={onSelect}
    >
      <span className="candidate-rank">{String(rank).padStart(2, "0")}</span>
      <span className="candidate-copy">
        <strong>{props.candidate_name}</strong>
        <small>
          {props.district_label} · {formatNumber(props.all_pop, lang)}
          {bilingual(lang, " 到访", " visits")}
        </small>
      </span>
      <ScoreBadge score={score} />
      <ChevronRight size={16} />
    </button>
  );
}

function Metric({ value, label, tone = "default" }) {
  return (
    <div className={`metric-cell ${tone}`}>
      <strong>{value}</strong>
      <span>{label}</span>
    </div>
  );
}

function CandidateDetail({ feature, lang, role }) {
  if (!feature) return null;
  const props = feature.properties || {};
  const homeDominant = Number(props.home_share) >= Number(props.work_share);
  const evidence = [
    homeDominant
      ? bilingual(
          lang,
          `居住人口 ${formatNumber(props.home_pop, lang)}`,
          `${formatNumber(props.home_pop, lang)} home population`,
        )
      : bilingual(
          lang,
          `工作人口 ${formatNumber(props.work_pop, lang)}`,
          `${formatNumber(props.work_pop, lang)} work population`,
        ),
    bilingual(
      lang,
      `最近在营店 ${formatDistance(props.nearest_active_m, lang)}`,
      `Nearest active store ${formatDistance(props.nearest_active_m, lang)}`,
    ),
    bilingual(
      lang,
      `1 公里内 ${props.active_1km} 家在营店`,
      `${props.active_1km} active stores within 1 km`,
    ),
  ];
  const risk =
    Number(props.nearest_closed_m) < 1800
      ? bilingual(
          lang,
          "附近存在历史关闭位置，需核验搬迁、租约与经营原因。",
          "A historic closure is nearby; verify relocation, lease and operating causes.",
        )
      : role.experimental
        ? bilingual(
            lang,
            "专项运动设施与社群数据尚未接入，本排序只用于探索。",
            "Sport facilities and community activity data are not yet connected; exploratory only.",
          )
        : bilingual(
            lang,
            "仍需核验物业面积、租金、停车与真实到达动线。",
            "Still requires property size, rent, parking and real access verification.",
          );

  return (
    <section className="candidate-detail">
      <div className="detail-heading">
        <div>
          <span className="eyebrow">{role[lang === "zh" ? "zh" : "en"]}</span>
          <h2>{props.candidate_name}</h2>
          <p>{props.district_label}</p>
        </div>
        <ScoreBadge score={props[role.score]} />
      </div>
      <div className="metric-grid">
        <Metric
          value={formatNumber(props.all_pop, lang)}
          label={bilingual(lang, "综合到访", "All visits")}
        />
        <Metric
          value={formatNumber(props.home_pop, lang)}
          label={bilingual(lang, "居住人口", "Home pop.")}
        />
        <Metric
          value={formatNumber(props.work_pop, lang)}
          label={bilingual(lang, "工作人口", "Work pop.")}
        />
      </div>
      <div className="evidence-list">
        {evidence.map((item) => (
          <div key={item}>
            <Check size={15} />
            <span>{item}</span>
          </div>
        ))}
      </div>
      <div className="risk-note">
        <CircleHelp size={17} />
        <span>{risk}</span>
      </div>
    </section>
  );
}

function LocationWorkbench({
  data,
  lang,
  city,
  setCity,
  roleId,
  setRoleId,
  presentation,
}) {
  const [minPopulation, setMinPopulation] = useState(0);
  const [minGap, setMinGap] = useState(0);
  const [showActive, setShowActive] = useState(true);
  const [showClosed, setShowClosed] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const role = ROLE_OPTIONS.find((item) => item.id === roleId) || ROLE_OPTIONS[0];

  const cityCandidates = data.candidates[city] || EMPTY_FC;
  const cityMeta = data.meta.cities[city];
  const cityName = cityMeta[lang === "zh" ? "name_zh" : "name_en"];
  const cityStoreName = cityMeta.source_city;

  const cityStores = useMemo(
    () => ({
      type: "FeatureCollection",
      features: data.stores.features.filter(
        (feature) => feature.properties?.city === cityStoreName,
      ),
    }),
    [data.stores, cityStoreName],
  );

  const ranked = useMemo(() => {
    return cityCandidates.features
      .filter(
        (feature) =>
          Number(feature.properties?.all_pop || 0) >= minPopulation * 1000 &&
          Number(feature.properties?.nearest_active_m || 0) >= minGap * 1000,
      )
      .sort(
        (left, right) =>
          Number(right.properties?.[role.score] || 0) -
          Number(left.properties?.[role.score] || 0),
      );
  }, [cityCandidates, minPopulation, minGap, role.score]);

  const filteredCandidates = useMemo(
    () => ({ type: "FeatureCollection", features: ranked }),
    [ranked],
  );

  useEffect(() => {
    if (!ranked.length) {
      setSelectedId(null);
      return;
    }
    if (!ranked.some((feature) => feature.properties?.candidate_id === selectedId)) {
      setSelectedId(ranked[0].properties?.candidate_id);
    }
  }, [ranked, selectedId]);

  const selectedFeature = ranked.find(
    (feature) => feature.properties?.candidate_id === selectedId,
  );

  return (
    <main className={`workspace ${presentation ? "presentation-workspace" : ""}`}>
      {!presentation && (
        <aside className="control-rail">
          <div className="rail-section">
            <div className="section-label">
              <Map size={16} />
              {bilingual(lang, "市场", "Market")}
            </div>
            <div className="segmented">
              {["shanghai", "beijing"].map((key) => (
                <button
                  type="button"
                  className={city === key ? "active" : ""}
                  key={key}
                  onClick={() => setCity(key)}
                >
                  {data.meta.cities[key][lang === "zh" ? "name_zh" : "name_en"]}
                </button>
              ))}
            </div>
          </div>

          <div className="rail-section">
            <div className="section-label">
              <Crosshair size={16} />
              {bilingual(lang, "增长任务", "Growth mission")}
            </div>
            <div className="role-list">
              {ROLE_OPTIONS.map((option) => {
                const Icon = option.icon;
                return (
                  <button
                    type="button"
                    key={option.id}
                    className={roleId === option.id ? "active" : ""}
                    onClick={() => setRoleId(option.id)}
                    title={option[lang === "zh" ? "zh" : "en"]}
                  >
                    <Icon size={17} />
                    <span>
                      {option[lang === "zh" ? "shortZh" : "shortEn"]}
                    </span>
                    {option.experimental && <i>β</i>}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="rail-section">
            <div className="section-label">
              <SlidersHorizontal size={16} />
              {bilingual(lang, "空间过滤", "Spatial filters")}
            </div>
            <label className="slider-row">
              <span>
                {bilingual(lang, "最低综合到访", "Minimum visits")}
                <b>{minPopulation === 0 ? bilingual(lang, "不限", "Any") : `${minPopulation}k`}</b>
              </span>
              <input
                type="range"
                min="0"
                max="60"
                step="5"
                value={minPopulation}
                onChange={(event) => setMinPopulation(Number(event.target.value))}
              />
            </label>
            <label className="slider-row">
              <span>
                {bilingual(lang, "距在营店至少", "Min. active-store gap")}
                <b>{minGap === 0 ? bilingual(lang, "不限", "Any") : `${minGap} km`}</b>
              </span>
              <input
                type="range"
                min="0"
                max="4"
                step="0.5"
                value={minGap}
                onChange={(event) => setMinGap(Number(event.target.value))}
              />
            </label>
          </div>

          <div className="rail-section layer-section">
            <div className="section-label">
              <Layers3 size={16} />
              {bilingual(lang, "证据图层", "Evidence layers")}
            </div>
            <ToggleRow
              checked={showActive}
              onChange={setShowActive}
              icon={Store}
              label={bilingual(lang, "在营门店", "Active stores")}
              color="#087a52"
            />
            <ToggleRow
              checked={showClosed}
              onChange={setShowClosed}
              icon={Store}
              label={bilingual(lang, "历史关闭", "Historic closures")}
              color="#d94b3d"
            />
          </div>

          <div className="data-scope">
            <strong>{bilingual(lang, "准选址口径", "Pre-site scope")}</strong>
            <p>{data.meta.notes[lang]}</p>
          </div>
        </aside>
      )}

      <section className="map-shell">
        <MapCanvas
          lang={lang}
          center={cityMeta.center}
          zoom={cityMeta.zoom}
          candidates={filteredCandidates}
          points={cityStores}
          roleScore={role.score}
          selectedId={selectedId}
          onSelectCandidate={setSelectedId}
          showActive={showActive}
          showClosed={showClosed}
          storyKey={`${city}-${roleId}`}
        />
        <div className="map-title">
          <span>{cityName}</span>
          <strong>
            {bilingual(
              lang,
              `${role.zh}候选集店圈`,
              `${role.en} candidate trade areas`,
            )}
          </strong>
          <small>
            {bilingual(
              lang,
              `${ranked.length} 个候选 · 真实 Polygon`,
              `${ranked.length} candidates · real polygons`,
            )}
          </small>
        </div>
        <div className="map-legend">
          <span>
            <i className="gradient-scale" />
            {bilingual(lang, "低适配", "Lower fit")}
          </span>
          <span>{bilingual(lang, "高适配", "Higher fit")}</span>
        </div>
      </section>

      {!presentation && (
        <aside className="results-panel">
          <div className="results-summary">
            <div>
              <span className="eyebrow">{bilingual(lang, "推荐组合", "Candidate set")}</span>
              <h2>
                {ranked.length}
                <small>{bilingual(lang, " 个候选", " candidates")}</small>
              </h2>
            </div>
            <Search size={21} />
          </div>
          <CandidateDetail feature={selectedFeature} lang={lang} role={role} />
          <div className="list-heading">
            <strong>{bilingual(lang, "适配排序", "Fit ranking")}</strong>
            <span>{bilingual(lang, "演示性排序", "Demo ranking")}</span>
          </div>
          <div className="candidate-list">
            {ranked.slice(0, 20).map((feature, index) => (
              <CandidateRow
                key={feature.properties?.candidate_id}
                feature={feature}
                rank={index + 1}
                lang={lang}
                role={role}
                selected={feature.properties?.candidate_id === selectedId}
                onSelect={() => setSelectedId(feature.properties?.candidate_id)}
              />
            ))}
          </div>
        </aside>
      )}

      {presentation && selectedFeature && (
        <div className="story-caption location-caption">
          <span>{bilingual(lang, "中国候选集店圈", "China candidate trade area")}</span>
          <h1>{selectedFeature.properties?.candidate_name}</h1>
          <p>
            {bilingual(
              lang,
              `综合到访 ${formatNumber(selectedFeature.properties?.all_pop, lang)} · 最近在营店 ${formatDistance(selectedFeature.properties?.nearest_active_m, lang)}`,
              `${formatNumber(selectedFeature.properties?.all_pop, lang)} visits · nearest active store ${formatDistance(selectedFeature.properties?.nearest_active_m, lang)}`,
            )}
          </p>
        </div>
      )}
    </main>
  );
}

function SimilarityStrip({ item, index, lang, revealed }) {
  const overlap = Number(item["分布重合系数"] || 0);
  const labels = [
    bilingual(lang, "客流等级", "Traffic level"),
    bilingual(lang, "居住人口", "Home population"),
    bilingual(lang, "工作人口", "Work population"),
  ];
  return (
    <div className="similarity-strip">
      <div className="similarity-label">
        <span>{labels[index]}</span>
        <strong>{revealed ? `${Math.round(overlap * 100)}%` : "?"}</strong>
      </div>
      <div className={`distribution-track ${revealed ? "revealed" : ""}`}>
        <i className="distribution-a" />
        <i
          className="distribution-b"
          style={{ width: `${Math.max(35, overlap * 100)}%` }}
        />
      </div>
      <small>
        {revealed
          ? bilingual(lang, "在营与关闭分布重合", "active / closed distribution overlap")
          : bilingual(lang, "哪条是在营？", "Which one is active?")}
      </small>
    </div>
  );
}

function twinPointData(pair) {
  if (!pair) return EMPTY_FC;
  return {
    type: "FeatureCollection",
    features: [
      {
        type: "Feature",
        properties: {
          name: pair["在营门店"],
          status: "正常营业",
          city: pair["城市"],
        },
        geometry: {
          type: "Point",
          coordinates: [Number(pair["在营经度"]), Number(pair["在营纬度"])],
        },
      },
      {
        type: "Feature",
        properties: {
          name: pair["关闭门店"],
          status: "店铺关闭",
          city: pair["城市"],
        },
        geometry: {
          type: "Point",
          coordinates: [Number(pair["关闭经度"]), Number(pair["关闭纬度"])],
        },
      },
    ],
  };
}

function PopulationBlindTest({ data, lang, presentation }) {
  const [revealed, setRevealed] = useState(presentation);
  const [pairIndex, setPairIndex] = useState(0);
  const storyPairs = data.twins.filter((pair) => pair["周边场景相同"]).slice(0, 5);
  const pair = storyPairs[pairIndex] || data.twins[0];
  const pairCenter = pair
    ? [
        (Number(pair["在营经度"]) + Number(pair["关闭经度"])) / 2,
        (Number(pair["在营纬度"]) + Number(pair["关闭纬度"])) / 2,
      ]
    : [121.47, 31.23];

  useEffect(() => {
    if (presentation) setRevealed(true);
  }, [presentation]);

  return (
    <main className={`blind-page ${presentation ? "presentation-page" : ""}`}>
      <section className="blind-copy">
        <span className="eyebrow">
          {bilingual(lang, "人口盲测", "Population blind test")}
        </span>
        <h1>
          {bilingual(
            lang,
            "只看人口，你能分出哪组门店已经关闭吗？",
            "Can population alone tell which stores closed?",
          )}
        </h1>
        <p>
          {bilingual(
            lang,
            "394 家在营与关闭样本。先隐藏状态，只看客流、居住和工作人口等级。",
            "394 active and closed-store samples. Hide status and inspect traffic, home and work-population levels.",
          )}
        </p>
        <div className="similarity-panel">
          {data.similarity.map((item, index) => (
            <SimilarityStrip
              key={item["指标"]}
              item={item}
              index={index}
              lang={lang}
              revealed={revealed}
            />
          ))}
        </div>
        {!presentation && (
          <button
            type="button"
            className="primary-action"
            onClick={() => setRevealed(!revealed)}
          >
            {revealed ? <EyeOff size={18} /> : <Sparkles size={18} />}
            {revealed
              ? bilingual(lang, "重新盲测", "Reset test")
              : bilingual(lang, "揭晓答案", "Reveal answer")}
          </button>
        )}
        <div className="method-note">
          <CircleHelp size={16} />
          {bilingual(
            lang,
            "描述性分布重合，不是预测准确率，也不代表闭店因果。",
            "Descriptive overlap, not prediction accuracy or closure causality.",
          )}
        </div>
      </section>

      <section className="twin-stage">
        <div className="twin-map">
          <MapCanvas
            lang={lang}
            mode="blind"
            center={pairCenter}
            zoom={9.2}
            points={twinPointData(pair)}
            showActive
            showClosed
            storyKey={`${pairIndex}-${revealed}`}
          />
        </div>
        <div className="twin-overlay">
          <div className="twin-header">
            <div>
              <span>{bilingual(lang, "人口孪生门店", "Population twins")}</span>
              <strong>
                {pair?.["城市"]} · {pair?.["在营周边场景"]}
              </strong>
            </div>
            {!presentation && (
              <div className="pair-switcher">
                {storyPairs.map((item, index) => (
                  <button
                    type="button"
                    key={`${item["在营门店"]}-${item["关闭门店"]}`}
                    className={pairIndex === index ? "active" : ""}
                    onClick={() => setPairIndex(index)}
                    aria-label={`${index + 1}`}
                  >
                    {index + 1}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="level-triplet">
            <Metric
              value={pair?.["城市相对客流等级"]}
              label={bilingual(lang, "客流等级", "Traffic")}
            />
            <Metric
              value={pair?.["城市相对居住人口等级"]}
              label={bilingual(lang, "居住等级", "Home")}
            />
            <Metric
              value={pair?.["城市相对工作人口等级"]}
              label={bilingual(lang, "工作等级", "Work")}
            />
          </div>
          <div className={`twin-cards ${revealed ? "revealed" : ""}`}>
            <div>
              <span>A</span>
              <strong>{revealed ? pair?.["在营门店"] : bilingual(lang, "门店 A", "Store A")}</strong>
              <small>{revealed ? bilingual(lang, "在营", "Active") : "?"}</small>
            </div>
            <div>
              <span>B</span>
              <strong>{revealed ? pair?.["关闭门店"] : bilingual(lang, "门店 B", "Store B")}</strong>
              <small>{revealed ? bilingual(lang, "关闭", "Closed") : "?"}</small>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

function GlobalStories({
  data,
  lang,
  country,
  setCountry,
  snapshot,
  setSnapshot,
  presentation,
}) {
  const [showPopulation, setShowPopulation] = useState(true);
  const payload = data.global[country];
  const snapshots = payload?.snapshots || {};
  const points = snapshots[snapshot] || EMPTY_FC;
  const startCount = snapshots["202501"]?.features?.length || 0;
  const currentCount = snapshots["20260730"]?.features?.length || 0;
  const delta = currentCount - startCount;
  const countryName = payload?.[lang === "zh" ? "name_zh" : "name_en"];
  const populationGrid = data.populationGrids?.[country] || EMPTY_FC;
  const populationAvailable = populationGrid.features.length > 0;

  useEffect(() => {
    setShowPopulation(populationAvailable);
  }, [country, populationAvailable]);

  return (
    <main className={`global-page ${presentation ? "presentation-page" : ""}`}>
      {!presentation && (
        <aside className="country-rail">
          <span className="eyebrow">
            {bilingual(lang, "全球网络实验室", "Global network lab")}
          </span>
          <h1>
            {bilingual(
              lang,
              "其它国家不是答案，是可检验的参照系。",
              "Other markets are testable references, not copied answers.",
            )}
          </h1>
          <div className="country-list">
            {COUNTRY_ORDER.map((code) => {
              const item = data.global[code];
              const oldCount =
                item.snapshots?.["202501"]?.features?.length || 0;
              const newCount =
                item.snapshots?.["20260730"]?.features?.length || 0;
              return (
                <button
                  type="button"
                  key={code}
                  className={country === code ? "active" : ""}
                  onClick={() => setCountry(code)}
                >
                  <Globe2 size={18} />
                  <span>
                    <strong>{item[lang === "zh" ? "name_zh" : "name_en"]}</strong>
                    <small>
                      {oldCount} → {newCount}
                    </small>
                  </span>
                  <ChevronRight size={16} />
                </button>
              );
            })}
          </div>
          <div className="global-caveat">
            <CircleHelp size={16} />
            <p>
              {bilingual(
                lang,
                "快照变化用于观察网络形态，不直接视为官方开关店记录。",
                "Snapshot changes describe network form; they are not official opening/closure records.",
              )}
            </p>
          </div>
        </aside>
      )}

      <section className="global-map-shell">
        <MapCanvas
          lang={lang}
          mode="global"
          center={payload.center}
          zoom={payload.zoom}
          points={points}
          populationGrid={populationGrid}
          showPopulation={showPopulation}
          storyKey={`${country}-${snapshot}`}
        />
        {populationAvailable && !presentation && (
          <div className="population-map-control">
            <button
              type="button"
              className={showPopulation ? "active" : ""}
              onClick={() => setShowPopulation((value) => !value)}
            >
              <Layers3 size={16} />
              <span>{bilingual(lang, "人口网格", "Population grid")}</span>
              {showPopulation ? <Eye size={15} /> : <EyeOff size={15} />}
            </button>
            {showPopulation && (
              <div className="population-legend">
                <i className="population-ramp" />
                <div>
                  <span>500</span>
                  <span>2,500</span>
                  <span>10k</span>
                  <span>40k</span>
                  <span>120k+</span>
                </div>
              </div>
            )}
          </div>
        )}
        <div className="global-story-card">
          <span>{bilingual(lang, "真实迪卡侬网络", "Real Decathlon network")}</span>
          <h2>{countryName}</h2>
          <div className="network-delta">
            <strong>{points.features.length}</strong>
            <small>
              {snapshot === "202501"
                ? bilingual(lang, "2025 年 1 月位置", "locations in Jan 2025")
                : bilingual(lang, "2026 年 7 月位置", "locations in Jul 2026")}
            </small>
            <b className={delta >= 0 ? "positive" : "negative"}>
              {delta >= 0 ? "+" : ""}
              {delta}
            </b>
          </div>
          <p>{payload[lang === "zh" ? "story_zh" : "story_en"]}</p>
          <div className="population-source">
            <Users size={17} />
            <span className="population-source-copy">
              {payload[lang === "zh" ? "population_zh" : "population_en"]}
              <small>
                {payload[lang === "zh" ? "population_detail_zh" : "population_detail_en"]}
              </small>
            </span>
          </div>
        </div>
        {!presentation && (
          <div className="timeline-control">
            <button
              type="button"
              className={snapshot === "202501" ? "active" : ""}
              onClick={() => setSnapshot("202501")}
            >
              2025.01
            </button>
            <div className="timeline-line">
              <i className={snapshot === "20260730" ? "at-end" : ""} />
            </div>
            <button
              type="button"
              className={snapshot === "20260730" ? "active" : ""}
              onClick={() => setSnapshot("20260730")}
            >
              2026.07
            </button>
          </div>
        )}
      </section>

      {!presentation && (
        <aside className="global-details">
          <div className="detail-heading compact">
            <div>
              <span className="eyebrow">
                {bilingual(
                  lang,
                  "空间指纹 · 方法示意",
                  "Spatial fingerprint · illustrative",
                )}
              </span>
              <h2>{bilingual(lang, "先在本国归一化", "Normalize within market")}</h2>
            </div>
            <BarChart3 size={22} />
          </div>
          <div className="fingerprint-list">
            {[
              [bilingual(lang, "城市混合度", "Urban mix"), 78],
              [bilingual(lang, "家庭目的性", "Family destination"), country === "fr" ? 88 : 64],
              [bilingual(lang, "区域中心性", "Regional centrality"), country === "tw" ? 91 : 72],
              [bilingual(lang, "通勤履约能力", "Commute / pickup"), country === "sg" ? 93 : 61],
              [bilingual(lang, "门店网络增量", "Network increment"), delta > 0 ? 86 : 45],
            ].map(([label, value]) => (
              <div key={label}>
                <span>{label}</span>
                <i>
                  <b style={{ width: `${value}%` }} />
                </i>
                <strong>{value}</strong>
              </div>
            ))}
          </div>
          <div className="method-block">
            <strong>{bilingual(lang, "可迁移", "Transferable")}</strong>
            <p>
              {bilingual(
                lang,
                "门店角色、网络密度、商业锚点和城市内分位数。",
                "Store roles, network density, retail anchors and within-market percentiles.",
              )}
            </p>
          </div>
          <div className="method-block caution">
            <strong>{bilingual(lang, "不可照搬", "Do not copy")}</strong>
            <p>
              {bilingual(
                lang,
                "人口、收入、交通和面积的跨国绝对值。",
                "Absolute cross-country population, income, transport or area values.",
              )}
            </p>
          </div>
          <div className="store-sample">
            <strong>
              {bilingual(lang, "当前快照样本", "Current snapshot sample")}
            </strong>
            {points.features.slice(0, 7).map((feature, index) => (
              <div key={`${feature.properties?.name}-${index}`}>
                <MapPin size={14} />
                <span>{feature.properties?.name || "Decathlon"}</span>
              </div>
            ))}
          </div>
        </aside>
      )}

      {presentation && (
        <div className="story-caption global-caption">
          <span>{bilingual(lang, "全球网络状态", "Global network state")}</span>
          <h1>
            {countryName}: {startCount} → {currentCount}
          </h1>
          <p>{payload[lang === "zh" ? "story_zh" : "story_en"]}</p>
        </div>
      )}
    </main>
  );
}

function LoadingScreen({ lang, error }) {
  return (
    <div className="loading-screen">
      <div className="loading-mark">
        <MapPin size={26} />
      </div>
      <h1>
        {error
          ? bilingual(lang, "数据加载失败", "Data failed to load")
          : bilingual(lang, "正在组织位置证据", "Organizing location evidence")}
      </h1>
      <p>
        {error
          ? String(error)
          : bilingual(
              lang,
              "集店圈、门店网络与全球快照正在进入同一张地图。",
              "Trade areas, store networks and global snapshots are entering one map.",
            )}
      </p>
    </div>
  );
}

function Header({
  lang,
  setLang,
  view,
  setView,
  presentation,
  setPresentation,
}) {
  return (
    <header className="app-header">
      <div className="brand-lockup">
        <div className="brand-mark">
          <MapPin size={21} />
        </div>
        <div>
          <strong>极海</strong>
          <span>Jihai Location Intelligence</span>
        </div>
      </div>
      <nav className="main-nav" aria-label="Primary">
        {[
          ["location", Map, bilingual(lang, "准选址", "Candidate studio")],
          ["blind", BarChart3, bilingual(lang, "人口盲测", "Population test")],
          ["global", Globe2, bilingual(lang, "全球故事", "Global stories")],
        ].map(([id, Icon, label]) => (
          <button
            type="button"
            key={id}
            className={view === id ? "active" : ""}
            onClick={() => setView(id)}
          >
            <Icon size={17} />
            <span>{label}</span>
          </button>
        ))}
      </nav>
      <div className="header-actions">
        <button
          type="button"
          className="icon-text-button"
          onClick={() => setLang(lang === "zh" ? "en" : "zh")}
          title={lang === "zh" ? "Switch to English" : "切换为中文"}
        >
          <Languages size={17} />
          <span>{lang === "zh" ? "EN" : "中"}</span>
        </button>
        <button
          type="button"
          className="icon-text-button primary"
          onClick={() => setPresentation(!presentation)}
        >
          <Presentation size={17} />
          <span>{bilingual(lang, "故事模式", "Story mode")}</span>
        </button>
      </div>
    </header>
  );
}

function PresentationControls({
  lang,
  view,
  setView,
  setPresentation,
}) {
  const [playing, setPlaying] = useState(false);
  const index = STORY_ORDER.indexOf(view);
  const go = (direction) => {
    const nextIndex =
      (index + direction + STORY_ORDER.length) % STORY_ORDER.length;
    setView(STORY_ORDER[nextIndex]);
  };

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === "ArrowRight") go(1);
      if (event.key === "ArrowLeft") go(-1);
      if (event.key === "Escape") setPresentation(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index]);

  useEffect(() => {
    if (!playing) return undefined;
    const timer = window.setInterval(() => go(1), 9000);
    return () => window.clearInterval(timer);
  }, [playing, index]);

  return (
    <div className="presentation-controls">
      <button type="button" onClick={() => go(-1)} title="Previous">
        <ArrowLeft size={18} />
      </button>
      <span>
        {String(index + 1).padStart(2, "0")} / {String(STORY_ORDER.length).padStart(2, "0")}
      </span>
      <button type="button" onClick={() => go(1)} title="Next">
        <ArrowRight size={18} />
      </button>
      <button
        type="button"
        onClick={() => setPlaying(!playing)}
        title={bilingual(lang, "自动播放", "Autoplay")}
      >
        {playing ? <Pause size={17} /> : <Play size={17} />}
      </button>
      <button
        type="button"
        className="close-story"
        onClick={() => setPresentation(false)}
        title={bilingual(lang, "退出故事模式", "Exit story mode")}
      >
        <X size={18} />
      </button>
    </div>
  );
}

export default function App() {
  const initial = useMemo(readInitialState, []);
  const [lang, setLang] = useState(initial.lang);
  const [view, setView] = useState(initial.view);
  const [city, setCity] = useState(initial.city);
  const [role, setRole] = useState(initial.role);
  const [country, setCountry] = useState(initial.country);
  const [snapshot, setSnapshot] = useState(initial.snapshot);
  const [presentation, setPresentation] = useState(initial.presentation);
  const { data, error } = useSiteData();

  useEffect(() => {
    document.documentElement.lang = lang === "zh" ? "zh-CN" : "en";
    document.title = bilingual(
      lang,
      "极海 · 迪卡侬准选址实验室",
      "Jihai · Decathlon Location Lab",
    );
    const params = new URLSearchParams();
    params.set("view", view);
    params.set("lang", lang);
    if (view === "location") {
      params.set("city", city);
      params.set("role", role);
    }
    if (view === "global") {
      params.set("country", country);
      params.set("snapshot", snapshot);
    }
    if (presentation) params.set("story", "1");
    window.history.replaceState(null, "", `${window.location.pathname}?${params}`);
  }, [lang, view, city, role, country, snapshot, presentation]);

  if (!data || error) return <LoadingScreen lang={lang} error={error} />;

  return (
    <div className={`app ${presentation ? "presentation" : ""}`}>
      {!presentation && (
        <Header
          lang={lang}
          setLang={setLang}
          view={view}
          setView={setView}
          presentation={presentation}
          setPresentation={setPresentation}
        />
      )}
      {view === "location" && (
        <LocationWorkbench
          data={data}
          lang={lang}
          city={city}
          setCity={setCity}
          roleId={role}
          setRoleId={setRole}
          presentation={presentation}
        />
      )}
      {view === "blind" && (
        <PopulationBlindTest
          data={data}
          lang={lang}
          presentation={presentation}
        />
      )}
      {view === "global" && (
        <GlobalStories
          data={data}
          lang={lang}
          country={country}
          setCountry={setCountry}
          snapshot={snapshot}
          setSnapshot={setSnapshot}
          presentation={presentation}
        />
      )}
      {presentation && (
        <>
          <div className="story-brand">
            <MapPin size={18} />
            <strong>极海</strong>
            <span>Jihai Location Intelligence</span>
          </div>
          <PresentationControls
            lang={lang}
            view={view}
            setView={setView}
            setPresentation={setPresentation}
          />
        </>
      )}
    </div>
  );
}
